/* Events overview (row list).
   Rendering: Liquid renders up to 50 rows server-side (fast paint + SEO/JSON-LD + no-JS fallback).
   Because Liquid caps shop.metaobjects.<type>.values at 50, on connect we fetch the FULL set via the
   Storefront API (paginated, no cap) and re-render both lists client-side, then sort, insert Danish
   month dividers, wire the region filter (pills + mobile dropdown), the "show past" toggle, waitlist,
   and deduped interest clicks. Deep-links: ?region=<slug> or ?zip=<code> pre-filter the list for ads. */
var DA_MONTHS = ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december'];
var MONTHS_SHORT_DA = ['jan.', 'feb.', 'mar.', 'apr.', 'maj', 'jun.', 'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.'];
var WEEKDAYS_DA = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
var REGION_SLUGS = { 'Hovedstaden': 'hovedstaden', 'Sjælland': 'sjaelland', 'Syddanmark': 'syddanmark', 'Midtjylland': 'midtjylland', 'Nordjylland': 'nordjylland' };

function regionFromZip(zip) {
  var z = parseInt(String(zip || '').replace(/\D/g, ''), 10);
  if (!z) return '';
  if (z >= 1000 && z <= 3999) return 'Hovedstaden';
  if (z >= 4000 && z <= 4999) return 'Sjælland';
  if (z >= 5000 && z <= 6999) return 'Syddanmark';
  if (z >= 7000 && z <= 8999) return 'Midtjylland';
  if (z >= 9000 && z <= 9999) return 'Nordjylland';
  return '';
}
function escHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}
function haversineKm(lat1, lng1, lat2, lng2) {
  var R = 6371, rad = Math.PI / 180;
  var dLat = (lat2 - lat1) * rad, dLng = (lng2 - lng1) * rad;
  var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.sqrt(a));
}

// The events page is a landing page — it should open at the top. Guard against (a) the browser
// restoring a previous scroll position on reload and (b) a late-injected form (Shopify Forms /
// Klaviyo) grabbing focus and yanking the page down. Releases the instant the user scrolls/taps.
(function () {
  if (typeof window === 'undefined' || window.location.hash) return;
  var pinning = true;
  var release = function () { pinning = false; };
  ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (ev) {
    window.addEventListener(ev, release, { once: true, passive: true });
  });
  var pin = function () { if (pinning && window.scrollY > 0) window.scrollTo(0, 0); };
  window.addEventListener('load', function () {
    pin();
    var ticks = 0;
    var id = setInterval(function () { pin(); if (!pinning || ++ticks > 8) clearInterval(id); }, 200);
  });
})();

class EventOverview extends HTMLElement {
  connectedCallback() {
    this.endpoint = this.dataset.endpoint || '';
    this.sfToken = this.dataset.sfToken || '';
    this.sfVersion = this.dataset.sfVersion || '2025-01';
    this.ctaLabel = this.dataset.ctaLabel || 'Køb billet';
    this.fewLeft = parseInt(this.dataset.fewLeft, 10) || 5;
    this.pastLimit = parseInt(this.dataset.pastLimit, 10) || 12;
    this.activeRegion = '';

    this.upcomingBlock = this.querySelector('[data-block="upcoming"]');
    this.pastBlock = this.querySelector('[data-block="past"]');
    this.regionSelect = this.querySelector('[data-region-select]');
    this.toggleWrap = this.querySelector('[data-past-toggle]');

    // Region filter (pills + mobile dropdown, kept in sync).
    var bar = this.querySelector('[data-filter]');
    if (bar) bar.addEventListener('click', (e) => {
      var btn = e.target.closest('[data-region-btn]');
      if (btn) this.setRegion(btn.dataset.region || '');
    });
    if (this.regionSelect) this.regionSelect.addEventListener('change', () => this.setRegion(this.regionSelect.value));

    var toggle = this.querySelector('[data-show-past]');
    if (toggle && this.pastBlock) toggle.addEventListener('change', () => {
      this.pastBlock.hidden = !toggle.checked;
      if (toggle.checked) this.pastBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // Delegated: signup CTA + interest clicks + waitlist. Works for server- and client-rendered rows.
    this.addEventListener('click', (e) => {
      var signup = e.target.closest('[data-scroll-signup]');
      if (signup) { this.scrollToSignup(); return; }
      var zr = e.target.closest('[data-zip-reset]');
      if (zr) { this.clearZip(); return; }
      var dt = e.target.closest('[data-desc-toggle]');
      if (dt) {
        var drow = dt.closest('[data-item]'), panel = drow && drow.querySelector('[data-desc]');
        if (panel) { panel.hidden = !panel.hidden; dt.setAttribute('aria-expanded', String(!panel.hidden)); }
        return;
      }
      var link = e.target.closest('[data-interest-link]');
      if (link) { this.bumpInterest(link.closest('[data-item]')); return; }
      var wl = e.target.closest('[data-waitlist-toggle]');
      if (wl) { this.toggleWaitlist(wl); return; }
    });
    this.addEventListener('submit', (e) => {
      var form = e.target.closest('[data-waitlist-form]');
      if (form) this.submitWaitlist(e, form);
    });

    this.readUrl();
    this.hydrate();
  }

  // ---- Load the full event set (Storefront API) then finalize; fall back to the server rows. ----
  async hydrate() {
    var events = null;
    if (this.sfToken) { try { events = await this.fetchAll(); } catch (e) { events = null; } }
    if (events && events.length) this.renderAll(events);
    this.finalize();
  }

  async fetchAll() {
    var url = window.location.origin + '/api/' + this.sfVersion + '/graphql.json';
    var q = 'query($c:String){ metaobjects(type:"event", first:250, after:$c){ pageInfo{ hasNextPage endCursor } nodes{ handle'
      + ' title:field(key:"title"){value} type:field(key:"event_type"){value} host:field(key:"host"){value}'
      + ' date:field(key:"event_date"){value} time:field(key:"event_time"){value} city:field(key:"city"){value}'
      + ' price:field(key:"price"){value} region:field(key:"region"){value} zip:field(key:"zip"){value}'
      + ' lat:field(key:"latitude"){value} lng:field(key:"longitude"){value}'
      + ' desc:field(key:"description"){value} soldout:field(key:"sold_out"){value}'
      + ' spaces:field(key:"spaces_left"){value}'
      + ' url:field(key:"retailer_url"){value} interest:field(key:"interest_count"){value} } } }';
    var out = [], cursor = null, guard = 0;
    do {
      var res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': this.sfToken },
        body: JSON.stringify({ query: q, variables: { c: cursor } }),
      });
      var j = await res.json();
      if (!j.data || !j.data.metaobjects) break;
      j.data.metaobjects.nodes.forEach((n) => out.push(n));
      cursor = j.data.metaobjects.pageInfo.hasNextPage ? j.data.metaobjects.pageInfo.endCursor : null;
    } while (cursor && ++guard < 8);
    var v = (f) => (f && f.value != null ? f.value : '');
    return out.map((n) => ({
      handle: n.handle, title: v(n.title), type: v(n.type), host: v(n.host), date: v(n.date),
      time: v(n.time), city: v(n.city), price: v(n.price), region: v(n.region), zip: v(n.zip),
      lat: v(n.lat), lng: v(n.lng), desc: v(n.desc), soldOut: v(n.soldout) === 'true',
      url: v(n.url), interest: parseInt(v(n.interest), 10) || 0,
      spaces: v(n.spaces) === '' ? null : parseInt(v(n.spaces), 10) || 0,
    })).filter((e) => e.title && e.date);
  }

  today() {
    var d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  renderAll(events) {
    var today = this.today();
    var up = events.filter((e) => e.date >= today);
    var past = events.filter((e) => e.date < today);
    var ul = this.querySelector('[data-list="upcoming"]');
    var pl = this.querySelector('[data-list="past"]');
    if (ul) ul.innerHTML = up.map((e) => this.rowHTML(e, false)).join('');
    if (pl) pl.innerHTML = past.map((e) => this.rowHTML(e, true)).join('');
    this.updateCounts(up);
    // Past section + toggle only exist when there are past events.
    var hasPast = past.length > 0;
    if (this.toggleWrap) this.toggleWrap.hidden = !hasPast;
    if (this.pastBlock && !hasPast) this.pastBlock.hidden = true;
  }

  rowHTML(e, past) {
    var p = e.date.split('-'), y = +p[0], mo = +p[1], da = +p[2];
    var monthShort = MONTHS_SHORT_DA[mo - 1] || '';
    var weekday = WEEKDAYS_DA[new Date(y, mo - 1, da).getDay()] || '';
    var meta = [];
    if (weekday) meta.push(escHtml(weekday));
    if (e.time) meta.push(escHtml(e.time));
    if (e.city) meta.push(escHtml(e.city));
    if (e.host) meta.push('med ' + escHtml(e.host));
    if (e.price) meta.push(escHtml(e.price));
    if (!past && e.interest > 0) meta.push('<strong class="event-row__interest"><span data-interest-num>' + e.interest + '</span> interesserede</strong>');

    var bell = '<svg class="event-row__soon-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 22a2.4 2.4 0 0 0 2.4-2h-4.8A2.4 2.4 0 0 0 12 22Zm6.3-6.2V11a6.3 6.3 0 0 0-4.6-6.05V4.6a1.7 1.7 0 0 0-3.4 0v.35A6.3 6.3 0 0 0 5.7 11v4.8l-1.5 1.5v.9h15.6v-.9Z"/></svg>';
    var action;
    if (past) action = '<span class="event-row__afholdt">Afholdt</span>';
    else if (e.soldOut) action = '<span class="event-row__udsolgt">Udsolgt</span><button type="button" class="event-row__notify-toggle" data-waitlist-toggle aria-expanded="false">Få besked om kommende events →</button>';
    else if (e.url) {
      var few = '';
      if (e.spaces != null && e.spaces > 0 && e.spaces <= this.fewLeft) {
        var kun = e.spaces <= 5 ? 'Kun ' : '';
        few = '<span class="event-row__fewleft">' + kun + e.spaces + (e.spaces === 1 ? ' plads' : ' pladser') + ' tilbage</span>';
      }
      action = few + '<a href="' + escHtml(e.url) + '" class="btn event-row__btn" data-interest-link target="_blank" rel="noopener">' + escHtml(this.ctaLabel) + '</a>';
    }
    else action = '<button type="button" class="btn event-row__btn event-row__btn--soon" data-waitlist-toggle aria-expanded="false" title="Skriv dig op og få besked, når billetsalget åbner"><span>Billetsalg åbner snart</span>' + bell + '</button>';

    var showForm = !past && (e.soldOut || !e.url);
    var lead = e.soldOut ? 'Udsolgt – skriv dig op og få besked, når vi holder et event nær dig igen.' : 'Skriv dig op, så giver vi dig besked, når billetsalget åbner.';
    var waitlist = '';
    if (showForm) waitlist =
      '<form class="event-row__waitlist" data-waitlist-form hidden>'
      + '<p class="event-row__waitlist-lead">' + lead + '</p>'
      + '<div class="event-row__waitlist-fields"><input type="text" class="input-field" required placeholder="Dit navn" data-wl-name autocomplete="name"><input type="text" class="input-field event-row__waitlist-zip" required placeholder="Postnr." data-wl-zip inputmode="numeric" pattern="[0-9]{4}" maxlength="4" autocomplete="postal-code"><input type="email" class="input-field" required placeholder="Din e-mail" data-wl-email autocomplete="email"><button type="submit" class="btn">Skriv mig op</button></div>'
      + '<label class="event-row__waitlist-consent"><input type="checkbox" required data-wl-consent><span>Ja tak, giv mig besked om events og nyheder fra ECOOKING. Kan afmeldes når som helst.</span></label>'
      + '<input type="text" class="event-row__hp" data-wl-hp tabindex="-1" autocomplete="off" aria-hidden="true">'
      + '<p class="event-row__waitlist-msg" data-wl-msg role="status"></p></form>';

    var descToggle = e.desc ? '<button type="button" class="event-row__desc-toggle" data-desc-toggle aria-expanded="false">Mere info <svg class="event-row__desc-caret" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" focusable="false"><path fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" d="M6 9l6 6 6-6"/></svg></button>' : '';
    var descPanel = e.desc ? '<div class="event-row__desc" data-desc hidden>' + escHtml(e.desc) + '</div>' : '';

    return '<div class="event-row' + (past ? ' event-row--past' : '') + '" data-item data-region="' + escHtml(e.region) + '" data-date="' + escHtml(e.date) + '" data-handle="' + escHtml(e.handle) + '" data-lat="' + escHtml(e.lat) + '" data-lng="' + escHtml(e.lng) + '">'
      + '<div class="event-row__date" aria-hidden="true"><span class="event-row__day">' + da + '</span><span class="event-row__month">' + monthShort + '</span></div>'
      + '<div class="event-row__info"><p class="event-row__title">' + (e.type ? escHtml(e.type) + ' · ' : '') + escHtml(e.title) + '</p>'
      + '<p class="event-row__meta secondary-item-color">' + meta.join(' · ') + '</p>' + descToggle + '</div>'
      + '<div class="event-row__action">' + action + '</div>'
      + descPanel + waitlist + '</div>';
  }

  // Recompute per-region counts from the full upcoming set (server counts were capped at 50).
  updateCounts(up) {
    var byRegion = {};
    up.forEach((e) => { byRegion[e.region] = (byRegion[e.region] || 0) + 1; });
    this.querySelectorAll('[data-region-btn]').forEach((btn) => {
      var r = btn.dataset.region || '';
      var n = r === '' ? up.length : (byRegion[r] || 0);
      var span = btn.querySelector('.event-overview__filter-count');
      if (span) span.textContent = '(' + n + ')';
    });
    if (this.regionSelect) Array.from(this.regionSelect.options).forEach((opt) => {
      var r = opt.value || '';
      var n = r === '' ? up.length : (byRegion[r] || 0);
      opt.textContent = opt.textContent.replace(/\s*\(\d+\)\s*$/, '') + ' (' + n + ')';
    });
  }

  finalize() {
    this.sortAndGroup(this.querySelector('[data-list="upcoming"]'), 'asc');
    this.sortAndGroup(this.querySelector('[data-list="past"]'), 'desc', this.pastLimit);
    this.rows = Array.from(this.querySelectorAll('[data-item]'));
    this.dividers = Array.from(this.querySelectorAll('.event-overview__month'));
    this.setRegion(this.initialRegion || '', true);
    if (this.zipQuery) this.applyZip();
  }

  sortAndGroup(list, dir, cap) {
    if (!list) return;
    Array.from(list.querySelectorAll('.event-overview__month')).forEach((d) => d.remove());
    var rows = Array.from(list.querySelectorAll('[data-item]'));
    rows.forEach((r) => { delete r.dataset.capped; });
    rows.sort((a, b) => {
      var da = a.dataset.date || '', db = b.dataset.date || '';
      return dir === 'asc' ? da.localeCompare(db) : db.localeCompare(da);
    });
    var lastMonth = null;
    rows.forEach((row, i) => {
      if (cap && i >= cap) row.dataset.capped = '1';
      var ym = (row.dataset.date || '').slice(0, 7);
      if (ym && ym !== lastMonth) { list.appendChild(this.monthDivider(ym)); lastMonth = ym; }
      list.appendChild(row);
    });
  }

  monthDivider(ym) {
    var parts = ym.split('-');
    var el = document.createElement('div');
    el.className = 'event-overview__month';
    el.dataset.month = ym;
    el.textContent = ((DA_MONTHS[parseInt(parts[1], 10) - 1] || '') + ' ' + parts[0]).toUpperCase();
    return el;
  }

  // ---- URL deep-links (?region=<slug> or ?zip=<code>) ----
  readUrl() {
    var p = new URLSearchParams(window.location.search);
    this.initialRegion = p.get('region') ? this.slugToRegion(p.get('region')) : '';
    var zip = (p.get('zip') || '').replace(/\D/g, '');
    // ?zip=<code>[&radius=<km>] -> proximity filter (default 10 km). Overrides region.
    this.zipQuery = zip ? { zip: zip, radius: parseFloat(p.get('radius')) || 10 } : null;
  }

  // Resolve the URL postcode to a coordinate (DAWA, public + CORS), then filter by distance.
  async applyZip() {
    if (!this.zipQuery) return;
    if (!this.zipCoord) {
      try {
        // Use a real ADDRESS in the postcode (accurate town location). DAWA's postcode `visueltcenter`
        // is a label-placement point that can sit far offshore/off-town (e.g. 3700 Rønne lands ~23 km
        // out at sea), which broke the radius filter. per_side=1, WGS84, mini struktur.
        var res = await fetch('https://api.dataforsyningen.dk/adgangsadresser?postnr=' + encodeURIComponent(this.zipQuery.zip) + '&per_side=1&srid=4326&struktur=mini');
        if (res.ok) {
          var arr = await res.json();
          if (arr && arr[0] && arr[0].x && arr[0].y) {
            this.zipCoord = { lng: arr[0].x, lat: arr[0].y };
            this.zipInfo = { nr: this.zipQuery.zip, navn: arr[0].postnrnavn || '' };
          }
        }
      } catch (e) { /* ignore */ }
    }
    if (!this.zipCoord) { this.zipQuery = null; return; } // couldn't resolve -> no filter
    this.setRegion('', true);   // proximity ignores the region pills; apply() adds the distance test
    this.renderZipNote();
  }

  clearZip() {
    this.zipQuery = null; this.zipCoord = null; this.zipInfo = null;
    var url = new URL(window.location.href);
    url.searchParams.delete('zip'); url.searchParams.delete('radius');
    window.history.replaceState(null, '', url);
    this.renderZipNote();
    this.apply();
  }

  renderZipNote() {
    var el = this.querySelector('[data-zip-note]');
    if (!el) return;
    if (this.zipQuery && this.zipCoord && this.zipInfo) {
      el.innerHTML = 'Viser events inden for <strong>' + this.zipQuery.radius + ' km</strong> af '
        + escHtml(this.zipInfo.nr + ' ' + this.zipInfo.navn)
        + ' <button type="button" class="event-overview__zipreset" data-zip-reset>Vis alle events</button>';
      el.hidden = false;
    } else { el.hidden = true; }
  }
  slugToRegion(slug) {
    slug = String(slug || '').trim().toLowerCase();
    for (var name in REGION_SLUGS) if (REGION_SLUGS[name] === slug || name.toLowerCase() === slug) return name;
    return '';
  }
  syncUrl() {
    var url = new URL(window.location.href);
    var slug = REGION_SLUGS[this.activeRegion];
    if (slug) url.searchParams.set('region', slug); else url.searchParams.delete('region');
    url.searchParams.delete('zip');
    window.history.replaceState(null, '', url);
  }

  setRegion(region, fromUrl) {
    this.activeRegion = region || '';
    // A manual region click exits proximity mode.
    if (!fromUrl && this.zipQuery) { this.zipQuery = null; this.zipCoord = null; this.zipInfo = null; this.renderZipNote(); }
    this.querySelectorAll('[data-region-btn]').forEach((b) => b.classList.toggle('is-active', (b.dataset.region || '') === this.activeRegion));
    if (this.regionSelect && this.regionSelect.value !== this.activeRegion) this.regionSelect.value = this.activeRegion;
    if (!fromUrl) this.syncUrl();
    this.apply();
  }

  apply() {
    var region = this.activeRegion;
    var zc = this.zipCoord, radius = this.zipQuery ? this.zipQuery.radius : 0;
    var upcoming = 0, visibleMonths = {};
    (this.rows || []).forEach((row) => {
      var show = (!region || row.dataset.region === region) && row.dataset.capped !== '1';
      if (show && zc) {
        var la = parseFloat(row.dataset.lat), lo = parseFloat(row.dataset.lng);
        show = !isNaN(la) && !isNaN(lo) && haversineKm(zc.lat, zc.lng, la, lo) <= radius;
      }
      row.hidden = !show;
      if (show) {
        visibleMonths[(row.dataset.date || '').slice(0, 7)] = true;
        if (!row.classList.contains('event-row--past')) upcoming += 1;
      }
    });
    (this.dividers || []).forEach((d) => { d.hidden = !visibleMonths[d.dataset.month]; });
    if (this.upcomingBlock) this.upcomingBlock.toggleAttribute('data-empty', upcoming === 0);
  }

  // Scroll to the signup form: walk to the next section holding an email field (the page has many
  // forms — search/cart/Klaviyo), stopping before the footer so we never land on the footer newsletter.
  scrollToSignup() {
    var target = document.getElementById('event-signup');
    if (!target) {
      var section = this.closest('.shopify-section') || this;
      var sib = section.nextElementSibling, firstNext = sib;
      while (sib) {
        if (sib.tagName === 'FOOTER' || (sib.querySelector && sib.querySelector('footer'))) break;
        if (sib.querySelector && sib.querySelector('input[type="email"], input[name*="email" i]')) { target = sib; break; }
        sib = sib.nextElementSibling;
      }
      if (!target) target = firstNext;
    }
    if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ---- Interest counter + waitlist ----
  post(payload) {
    if (!this.endpoint) return;
    try {
      fetch(this.endpoint, { method: 'POST', mode: 'no-cors', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: JSON.stringify(payload) }).catch(function () {});
    } catch (e) { /* ignore */ }
  }
  bumpDisplay(row) {
    var el = row && row.querySelector('[data-interest-num]');
    if (el) { var n = parseInt(el.textContent, 10); if (!isNaN(n)) el.textContent = n + 1; }
  }
  bumpInterest(row) {
    if (!row || !this.endpoint) return;
    var handle = row.dataset.handle;
    if (!handle) return;
    var key = 'eck-int-' + handle;
    try { if (localStorage.getItem(key)) return; localStorage.setItem(key, '1'); } catch (e) {}
    this.post({ action: 'interest', handle: handle });
    this.bumpDisplay(row);
  }
  toggleWaitlist(btn) {
    var row = btn.closest('[data-item]');
    var form = row && row.querySelector('[data-waitlist-form]');
    if (!form) return;
    form.hidden = !form.hidden;
    btn.setAttribute('aria-expanded', String(!form.hidden));
    if (!form.hidden) { var i = form.querySelector('[data-wl-email]'); if (i) i.focus(); }
  }
  submitWaitlist(e, form) {
    e.preventDefault();
    var row = form.closest('[data-item]');
    var msg = form.querySelector('[data-wl-msg]');
    if (form.querySelector('[data-wl-hp]').value) return; // honeypot -> bot
    if (!form.querySelector('[data-wl-consent]').checked) { msg.textContent = 'Sæt flueben for at modtage besked.'; return; }
    if (!this.endpoint) { msg.textContent = 'Tilmelding er ikke tilgængelig lige nu.'; return; }
    var email = (form.querySelector('[data-wl-email]').value || '').trim();
    var nameEl = form.querySelector('[data-wl-name]');
    var name = nameEl ? (nameEl.value || '').trim() : '';
    var zipEl = form.querySelector('[data-wl-zip]');
    var zip = zipEl ? (zipEl.value || '').trim() : '';
    this.post({ action: 'waitlist', handle: row.dataset.handle, email: email, name: name, zip: zip, hp: '' });
    var fields = form.querySelector('.event-row__waitlist-fields');
    var consent = form.querySelector('.event-row__waitlist-consent');
    if (fields) fields.hidden = true;
    if (consent) consent.hidden = true;
    msg.textContent = 'Tak! Vi giver dig besked, når billetsalget åbner.';
    this.bumpDisplay(row);
  }
}

if (!customElements.get('event-overview')) {
  customElements.define('event-overview', EventOverview);
}
